<script setup lang="ts">
import RelatedPosts from "@/components/Blog/RelatedPosts.vue";
import IconeBds from "@/assets/img/icone_BDS.png";
import IconeBdsGold from "@/assets/img/icone-bds-gold.png";
import BannerAssineHorizontal from "@/components/HomePage/BannerAssineHorizontal.vue";
import JsonLD from "@/components/JsonLD.vue";
import imgWhatsapp from "@/assets/img/whatsappRed.png";
import translaters from "@/tradutors/blogs";
import api from "@/api";
import vueRecaptcha from "vue3-recaptcha2";
import { isMobile } from "@/shared/utils/mobile";
import { renderCommentHtml } from "@/shared/utils/commentHtml";
import { marcaProporcaoDosVideos } from "@/utils/videoDoPost";

const cookie = useCookie("commentUser", {
  expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 7),
});

const route = useRoute();
const canonical = useCanonicalUrl();
const { user, setUser } = userStore();

const loadingButton = ref(false);
const showButton = ref(true);
const showRecaptchaAnswer = ref(true);
const showRecaptcha = ref(true);
const relatedLimit = ref(isMobile() ? 4 : 9);
const relatedPosts = ref([]);
const bannerAssineHorizontal = ref("");
const red = ref<any>(null);
const redAnswer = ref<any>(null);
const recaptcha = ref(null);
const recaptchaAnswer = ref(null);
const recaptchaSiteKey = "6LcqxykUAAAAADAOWqu2zCV2_uaW1IMDzoiKfn5J";
const nomeComment = ref<any>("");
const emailComment = ref("");
const newsletterComment = ref(true);
const comentario = ref("");
const nicknameComment = ref("");
const nicknameCommentShow = ref(false);
const nomeCommentAnswer = ref("");
const emailCommentAnswer = ref("");
const newsletterCommentAnswer = ref(true);
const comentarioAnswer = ref("");
const nicknameCommentAnswer = ref("");
const nicknameCommentAnswerShow = ref(false);
const answered_comment_id = ref("");
const router = useRouter();
const showShare = ref(false);
// Só é lido dentro do popup (v-if), portanto nunca avaliado no SSR.
const urlCopy = computed(() => window.location.href);

const translate = computed(() =>
  route.params.language === "pt-br" ? translaters.portugues : translaters.ingles
);

onMounted(() => {
  getBlogRelatedPosts(route.params.id);
  getBannerAssineHorizontal();
  marcaProporcaoDosVideos(
    document.querySelectorAll<HTMLVideoElement>("#slot-post-api video")
  );
});

definePageMeta({
  middleware: ["not-found-lang"],
});


const isEnglish = route.params.language == "en";
const orgUrl = isEnglish
  ? "https://bellaclub.com"
  : "https://belladasemana.com.br";
const publisherName = isEnglish ? "Bella Club" : "Bella da Semana";
const publisherLogo = "https://api.belladasemana.com.br/assets/img/bella512x512.png";
const mainEntityOfPage = canonical;

const { $getCachedData } = useNuxtApp();
const key = "blog" + "-" + route.params.language + "-" + route.params.id;
var cachedData = $getCachedData(key);
if (!cachedData) {
  const { data } = await useAsyncData(key, async () => {
    let url = "";
    if (route.query.preview == "true") url = "&preview=true";
    const blog = await api
      .get(
        `v1/blog/getBlogId/${route.params.id}?language=${route.params.language}${url}`,
        {
          headers: {
            "X-Custom-Data": cookie.value ?? "",
          },
        }
      )
      .then(({ data }) => {
        return data.blog[0];
      });

    return blog;
  });
  if (!data.value)
    throw createError({ statusCode: 404, statusMessage: "Page Not Found" });

  if (data.value.slug !== route.params.slug)
    router.replace({ params: { slug: data.value.slug } });
  cachedData = data.value;
}

const dadosSSR = ref(cachedData);
useHead({
  templateParams: {
    subTitle: null,
    subPage: dadosSSR.value.title ?? "",
    page_url: canonical,
    // sem descrição cadastrada, um trecho do post (vazio saía content="true")
    page_description: descricaoDaPagina(
      dadosSSR.value.meta_description,
      dadosSSR.value.content,
      dadosSSR.value.title ||
        (String(route.params.language) == "pt-br"
          ? "Fique por dentro do melhor do universo masculino com diversas matérias!"
          : "Stay on top of the best of the male universe with several subjects!")
    ),
    page_image: shareImage(dadosSSR.value.cover),
    page_keywords:
      dadosSSR.value.meta_keywords ||
      "bella, bella da semana, bela da semana, bella semana, bela semana, belladasemana, bella site, bella da semana site, bella blog",
    page_locale: route.params.language as string,
    page_url_pt:
      "https://belladasemana.com.br/pt-br/blog/" +
      route.params.id +
      "/" +
      dadosSSR.value.alternate_pt,
    page_url_en:
      "https://belladasemana.com.br/en/blog/" +
      route.params.id +
      "/" +
      dadosSSR.value.alternate_en,
    page_canonical: canonical,
  },
});

const blog = ref({
  title: dadosSSR.value.title,
  slug: dadosSSR.value.slug,
  content: dadosSSR.value.content,
  cover: dadosSSR.value.cover,
  date: dadosSSR.value.date,
  schedule: dadosSSR.value.schedule,
  comments: dadosSSR.value.comments,
  listComents: dadosSSR.value.listComents,
  meta_description: [
    {
      content: dadosSSR.value.meta_description[0]?.content,
    },
  ],
  meta_keywords: dadosSSR.value.meta_keywords,
  alternate_en: dadosSSR.value.alternate_en,
  alternate_pt: dadosSSR.value.alternate_pt,
  // Post ligado à live no ar: ganha o botão para a página da live (JanelaDaLive no backend).
  liveButton: dadosSSR.value.liveButton === true,
});

const jsonOptions = ref({
  type: "BlogPosting",
  headline: dadosSSR.value.title,
  datePublished: dadosSSR.value.scheduleTime,
  dateModified: dadosSSR.value.dateTime,
  image: dadosSSR.value.cover,
  author: publisherName,
  url: orgUrl,
  mainEntityOfPage,
  publisherName,
  publisherLogo,
});

function recaptchaExpired() {
  red.value.reset();
}
function recaptchaExpiredAnswer() {
  redAnswer.value.reset();
}
function recaptchaUpdated(recaptchaIn: any) {
  recaptcha.value = recaptchaIn;
}
function recaptchaUpdatedAnswer(recaptcha: any) {
  recaptchaAnswer.value = recaptcha;
}

async function getBannerAssineHorizontal(changeLanguage = false) {
  if (!sessionStorage.getItem("bannerAssineHorizontal") || changeLanguage)
    api
      .get(
        `v1/home/getBannerSuper?mobile=${isMobile()}&language=${
          route.params.language
        }`
      )
      .then(({ data }) => {
        bannerAssineHorizontal.value = data.bannerAssineHorizontal;
        sessionStorage.setItem(
          "bannerAssineHorizontal",
          JSON.stringify(bannerAssineHorizontal.value)
        );
      });
  else
    bannerAssineHorizontal.value = JSON.parse(
      sessionStorage.getItem("bannerAssineHorizontal") ?? ""
    );
}

async function seeMore() {
  relatedLimit.value += isMobile() ? 4 : 9;
  await getBlogRelatedPosts(route.params.id);
}

async function getBlogName(name: string | string[], isCreated: boolean) {
  let url = "";
  if (route.query.preview == "true") url = "&preview=true";
  api
    .get(
      `v1/blog/getBlogId/${name}?language=${
        isCreated ? route.params.locale : route.params.language
      }${url}`,
      {
        headers: {
          "X-Custom-Data": cookie.value ?? "",
        },
      }
    )
    .then(({ data }) => {
      if (data.blog[0]) {
        if (isCreated) {
          if (data.blog[0].slug === route.params.blogName) {
            data.blog[0].content = data.blog[0].content.replace(
              "https://admin",
              "https://api"
            );
            //   data.blog[0].content = this.transformImageTags(data.blog[0].content, (screen.width <= 412 ? 412 : (screen.width <= 640 ? 640 : 808)))
            blog.value = data.blog[0];
          } else {
            redirect("404");
          }
        } else {
          router
            .replace({ params: { blogName: data.blog[0].slug } })
            .finally(() => {
              if (data.blog[0].slug === route.params.blogName) {
                blog.value = data.blog[0];
              } else {
                redirect("404");
              }
            });
        }
        jsonOptions.value = {
          type: "BlogPosting",
          headline: data.blog[0].title,
          datePublished: data.blog[0].scheduleTime,
          dateModified: data.blog[0].dateTime,
          image: data.blog[0].cover,
          author: publisherName,
          url: orgUrl,
          mainEntityOfPage,
          publisherName,
          publisherLogo,
        };
      } else {
        redirect("404");
      }
    })
    .finally(() => getBlogRelatedPosts(route.params.id));
}

async function getBlogRelatedPosts(name: string | string[]) {
  loadingButton.value = true;
  api
    .get(
      `v1/blog/getBlogRelatedPosts/${name}?language=${route.params.language}&limit=${relatedLimit.value}`
    )
    .then(({ data }) => {
      relatedPosts.value = data.relatedPosts;
      loadingButton.value = false;
      if (relatedPosts.value.length < relatedLimit.value) {
        showButton.value = false;
      }
    });
}

function redirect(name: string) {
  router.push({
    name,
    params: {
      locale: route.params.language,
    },
  });
}

function saveComment(emitMessage: any) {
  let form: any = {
    nome: nomeComment.value,
    email: emailComment.value,
    newsletter: newsletterComment.value,
    comentario: comentario.value,
    answered_comment_id: "",
    api: true,
    locale: route.params.language,
  };

  if (nicknameCommentShow.value) {
    form["nickname"] = nicknameComment.value;
  } else {
    form["nickname"] = user.nickname;
  }

  form["g-recaptcha-response"] = recaptcha.value;

  if (!recaptcha.value)
    return emitMessage({
      message: {
        show: true,
        title: route.params.language == "pt-br" ? "Erro" : "Error",
        textButton: route.params.language == "pt-br" ? "Fechar" : "Close",
        textBoby:
          route.params.language == "pt-br"
            ? "Preencha o Recaptcha!"
            : "Complete the Recaptcha",
      },
    });

  api
    .post(
      `v1/blog/commentBlogId/${route.params.language}/${route.params.id}`,
      form
    )
    .then(({ data }) => {
      if (nicknameCommentShow.value && nicknameComment.value != "") {
        api.get("v1/myaccount/getUser").then(({ data }) => {
          setUser(data);
        });
      }
      emitMessage({
        message: {
          show: true,
          title: route.params.language == "pt-br" ? "Aviso" : "Notice",
          textButton: route.params.language == "pt-br" ? "Fechar" : "Close",
          textBoby: data.message,
        },
      });
      if (data.userId) {
        cookie.value = btoa(data.userId);
      }
      nomeComment.value = "";
      emailComment.value = "";
      comentario.value = "";
      nicknameComment.value = "";
      nicknameCommentShow.value = false;
    })
    .catch((err) => {
      if (err.response.status === 400 && err.response.data.message)
        emitMessage({
          message: {
            show: true,
            title: route.params.language == "pt-br" ? "Erro" : "Error",
            textButton: route.params.language == "pt-br" ? "Fechar" : "Close",
            textBoby: err.response.data.message,
          },
        });
      else
        emitMessage({
          message: {
            show: true,
            title: route.params.language == "pt-br" ? "Erro" : "Error",
            textButton: route.params.language == "pt-br" ? "Fechar" : "Close",
            textBoby:
              route.params.language == "pt-br"
                ? "Falha ao enviar comentario!"
                : "Failed to send comment!",
          },
        });
    });

  recaptchaExpired();
}
function saveCommentAnswer(emitMessage: any) {
  let form: any = {
    nome: nomeCommentAnswer.value,
    email: emailCommentAnswer.value,
    newsletter: newsletterCommentAnswer.value,
    comentario: comentarioAnswer.value,
    answered_comment_id: answered_comment_id.value,
    api: true,
    locale: route.params.language,
  };

  if (nicknameCommentAnswerShow.value) {
    form["nickname"] = nicknameCommentAnswer.value;
  } else {
    form["nickname"] = user.nickname;
  }

  form["g-recaptcha-response"] = recaptchaAnswer.value;

  if (!recaptchaAnswer.value)
    return emitMessage({
      message: {
        show: true,
        title: route.params.language == "pt-br" ? "Erro" : "Error",
        textButton: route.params.language == "pt-br" ? "Fechar" : "Close",
        textBoby:
          route.params.language == "pt-br"
            ? "Preencha o Recaptcha!"
            : "Complete the Recaptcha",
      },
    });

  api
    .post(
      `v1/blog/commentBlogId/${route.params.language}/${route.params.id}`,
      form
    )
    .then(({ data }) => {
      if (
        nicknameCommentAnswerShow.value &&
        nicknameCommentAnswer.value != ""
      ) {
        api.get("v1/myaccount/getUser").then(({ data }) => {
          setUser(data);
        });
      }
      emitMessage({
        message: {
          show: true,
          title: route.params.language == "pt-br" ? "Aviso" : "Notice",
          textButton: route.params.language == "pt-br" ? "Fechar" : "Close",
          textBoby: data.message,
        },
      });
      if (data.userId) {
        cookie.value = btoa(data.userId);
      }
      nomeCommentAnswer.value = "";
      emailCommentAnswer.value = "";
      comentarioAnswer.value = "";
      answered_comment_id.value = "";
      nicknameCommentAnswer.value = "";
      nicknameCommentAnswerShow.value = false;
    })
    .catch((err) => {
      if (err.response.status === 400 && err.response.data.message)
        emitMessage({
          message: {
            show: true,
            title: route.params.language == "pt-br" ? "Erro" : "Error",
            textButton: route.params.language == "pt-br" ? "Fechar" : "Close",
            textBoby: err.response.data.message,
          },
        });
      else
        emitMessage({
          message: {
            show: true,
            title: route.params.language == "pt-br" ? "Erro" : "Error",
            textButton: route.params.language == "pt-br" ? "Fechar" : "Close",
            textBoby:
              route.params.language == "pt-br"
                ? "Falha ao enviar comentario!"
                : "Failed to send comment!",
          },
        });
    });
  recaptchaExpiredAnswer();
}

function toggle(comment: any) {
  nomeCommentAnswer.value = "";
  emailCommentAnswer.value = "";
  newsletterCommentAnswer.value = true;
  comentarioAnswer.value = "";
  nicknameCommentAnswer.value = "";
  nicknameCommentAnswerShow.value = false;
  if (answered_comment_id.value == comment.id) {
    answered_comment_id.value = "";
  } else {
    answered_comment_id.value = comment.id;
  }
}
function showNickname(nickname: string) {
  nicknameComment.value = nickname;
  nicknameCommentShow.value = true;
}
function showNicknameAnswer(nickname: string) {
  nicknameCommentAnswer.value = nickname;
  nicknameCommentAnswerShow.value = true;
}

function openShare() {
  showShare.value = true;
}

function closeShare() {
  showShare.value = false;
}

function goToWhatsapp(title: string) {
  const url = window.location.href;
  const text =
    route.params.language == "pt-br"
      ? " - Blog do Bella da Semana "
      : " - Blog on Bella Club ";
  window.open(
    "https://api.whatsapp.com/send?text=" + encodeURIComponent(title + text + url)
  );
}

function copyToClipboard() {
  const url = window.location.href;
  navigator.clipboard.writeText(url).then(() => {
    alert(
      route.params.language == "pt-br"
        ? "Link copiado para a área de transferência!"
        : "Link copied to clipboard!"
    );
  });
}
</script>
<template>
  <JsonLD :jsonOptions="jsonOptions" />
  <NuxtLayout #="{ showModalMessages }">
    <div class="slot-body">
      <div class="container px-3">
        <div class="row pt-3 align-items-center justify-content-center">
          <div class="box-title pt-3">
            <h1 class="title">{{ blog.title }}</h1>
          </div>
        </div>
        <div class="row">
          <div
            v-if="blog.liveButton"
            class="col-lg-12 d-flex justify-content-center pt-3 pb-2"
          >
            <NuxtLink class="meuBotao botao-live" :to="`/${route.params.language}/live`">
              {{ translate.watchLive }}
            </NuxtLink>
          </div>
          <div
            class="col-lg-12 d-flex align-items-center justify-content-center"
          >
            <div id="slot-post-api" v-html="blog.content"></div>
          </div>
          <div
            class="col-lg-12 d-flex align-items-center justify-content-center"
          >
            <div class="box-details">
              <div class="row justify-content-space-between">
                <p class="card-text small col-lg-3 col-12">
                  <font-awesome :icon="['far', 'clock']" size="sm" />
                  {{ translate.publishedDate }} {{ blog.schedule }}
                </p>
                <p class="card-text small col-lg-4 col-12">
                  <font-awesome :icon="['far', 'clock']" size="sm" />
                  {{ translate.modifiedDate }} {{ blog.date }}
                </p>
                <p class="card-text small col-lg-2 col-12">
                  <font-awesome :icon="['far', 'comment-dots']" size="sm" />
                  {{ blog.comments }}
                  {{ translate.commentsBlog }}
                </p>
                <div class="card-text col-lg-3 col-12 share-align">
                  <span class="share-trigger" @click="openShare()">
                    <span>{{ translate.share }}</span>
                    <font-awesome
                      :icon="['fas', 'share-nodes']"
                      size="sm"
                      style="color: #b20000; margin-left: 3px"
                    />
                  </span>
                  <div class="share-popup" v-if="showShare">
                    <div>
                      <img
                        class="cursor-pointer"
                        :src="imgWhatsapp"
                        alt="WhatsApp"
                        width="50"
                        @click="goToWhatsapp(blog.title)"
                      />
                    </div>
                    <div class="share-close cursor-pointer" @click="closeShare()">
                      X
                    </div>
                    <div class="mt-3">
                      <input
                        class="share-input"
                        type="text"
                        :value="urlCopy"
                        readonly
                      />
                      <button
                        type="button"
                        class="share-button"
                        @click="copyToClipboard()"
                      >
                        {{ route.params.language == "pt-br" ? "Copiar" : "Copy" }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="col-lg-12 d-flex align-items-center justify-content-center"
          >
            <RelatedPosts :posts="relatedPosts" />
          </div>
          <div v-if="showButton">
            <div
              class="d-flex justify-content-center mt-3"
              v-if="!loadingButton"
            >
              <a class="button mt-2" href="#" @click.prevent="seeMore">
                {{ translate.labelButton }}
                <i class="icon-arrow-right"></i>
              </a>
            </div>
            <div v-else class="text-center spin-load my-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
          <div v-else class="mb-5"></div>
        </div>
      </div>
      <div class="box-gray px-3">
        <div class="container">
          <div class="row d-flex align-items-center justify-content-center">
            <div class="box-w">
              <span class="title-comments" v-if="blog.listComents.length">{{
                translate.commentAllBox
              }}</span>
              <div class="list-comentarios" v-if="blog.listComents.length">
                <div
                  v-for="(comment, index) in blog.listComents"
                  :key="index"
                  class="card-comment"
                >
                  <div>
                    <img
                      v-if="comment.ativo == 'true'"
                      :src="comment.gold == 'true' ? IconeBdsGold : IconeBds"
                      :class="{ 'icone-gold': comment.gold == 'true' }"
                      :alt="
                        comment.gold == 'true' ? 'Assinante Gold' : 'Assinante'
                      "
                      :title="
                        comment.gold == 'true' ? 'Assinante Gold' : 'Assinante'
                      "
                      width="12"
                      height="12"
                    />
                    <span>
                      <strong>
                        {{
                          comment.name
                            ? comment.name
                            : route.params.language == "pt-br"
                            ? "Anônimo"
                            : "Anonymous"
                        }}
                      </strong>
                    </span>
                    {{ translate.wroteOn }} {{ comment.date }}
                    <span class="a-answer text-answer" @click="toggle(comment)">
                      {{ translate.answer }}</span
                    >
                  </div>
                  <!-- Texto do comentário via v-html: o conteúdo é escapado em
                       renderCommentHtml() e só os links (tags <a> ou URLs
                       digitadas) são reconstruídos, sem risco de XSS. -->
                  <legend
                    :class="{ 'texto-gold': comment.gold == 'true' }"
                    v-html="renderCommentHtml(comment.comment)"
                  ></legend>
                  <hr />
                  <div
                    v-for="(commentAnswer, indexAnswer) in comment.answers"
                    :key="indexAnswer"
                    class="card-comment answer"
                  >
                    <div>
                      <img
                        v-if="commentAnswer.ativo == 'true'"
                        :src="
                          commentAnswer.gold == 'true' ? IconeBdsGold : IconeBds
                        "
                        :class="{ 'icone-gold': commentAnswer.gold == 'true' }"
                        :alt="
                          commentAnswer.gold == 'true'
                            ? 'Assinante Gold'
                            : 'Assinante'
                        "
                        :title="
                          commentAnswer.gold == 'true'
                            ? 'Assinante Gold'
                            : 'Assinante'
                        "
                        width="12"
                        height="12"
                      />
                      <span>
                        <strong>
                          {{
                            commentAnswer.name
                              ? commentAnswer.name
                              : route.params.language == "pt-br"
                              ? "Anônimo"
                              : "Anonymous"
                          }}
                        </strong>
                      </span>
                      {{ translate.wroteOn }} {{ commentAnswer.date }}
                    </div>
                    <legend
                      :class="{ 'texto-gold': commentAnswer.gold == 'true' }"
                      v-html="renderCommentHtml(commentAnswer.comment)"
                    ></legend>
                    <hr />
                  </div>
                  <div
                    v-if="answered_comment_id === comment.id"
                    class="box-w-2 answer"
                  >
                    <div class="questionario row">
                      <input
                        type="hidden"
                        class="form-control"
                        name="answered_comment_id"
                        v-model="answered_comment_id"
                      />
                      <span class="quest-title">
                        {{ translate.yoursComment }}</span
                      >
                      <span class="quest-nickname" v-if="user.name">
                        {{ translate.yourNickname }}
                        {{ user.nickname ?? user.name }} (
                        <a
                          @click="showNicknameAnswer(user.nickname ?? '')"
                          class="alter-nickname"
                          >{{ translate.alterNickName }}</a
                        >
                        )
                        <input
                          v-if="nicknameCommentAnswerShow"
                          class="form-control"
                          required
                          type="text"
                          v-model="nicknameCommentAnswer"
                        />
                      </span>
                      <div class="row" v-else>
                        <div class="col-6">
                          <div class="form-group">
                            <input
                              type="text"
                              class="form-control"
                              name="name"
                              :placeholder="translate.yourName"
                              v-model="nomeCommentAnswer"
                            />
                          </div>
                        </div>
                        <div class="col-6">
                          <div class="form-group">
                            <input
                              type="text"
                              class="form-control"
                              name="email"
                              :placeholder="translate.yourEmail"
                              v-model="emailCommentAnswer"
                            />
                          </div>
                        </div>
                        <div class="col-lg-6 col-12 accept mt-3">
                          <input
                            type="checkbox"
                            name="acept"
                            id="acept"
                            v-model="newsletterCommentAnswer"
                          />
                          <label for="checkbox">{{
                            translate.formNewsCheck
                          }}</label>
                        </div>
                      </div>
                      <div class="mb-3">
                        <textarea
                          class="form-control"
                          id="text-comment-bella"
                          :placeholder="translate.inputComment"
                          rows="5"
                          v-model="comentarioAnswer"
                        ></textarea>
                      </div>
                      <vue-recaptcha
                        ref="redAnswer"
                        v-if="showRecaptchaAnswer"
                        :sitekey="recaptchaSiteKey"
                        v-on:verify="recaptchaUpdatedAnswer"
                        hl="pt-br"
                      ></vue-recaptcha>
                      <a
                        class="button mt-2"
                        @click="saveCommentAnswer(showModalMessages)"
                      >
                        {{ translate.button }}
                        <i class="icon-arrow-right"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="box-w-2">
              <div class="questionario row">
                <span class="quest-title"> {{ translate.yoursComment }}</span>
                <span class="quest-nickname" v-if="user.name">
                  {{ translate.yourNickname }}
                  {{ user.nickname ?? user.name }} (
                  <a
                    @click="showNickname(user.nickname ?? '')"
                    class="alter-nickname"
                    >{{ translate.alterNickName }}</a
                  >
                  )
                  <input
                    v-if="nicknameCommentShow"
                    class="form-control"
                    required
                    type="text"
                    v-model="nicknameComment"
                  />
                </span>
                <div class="row" v-else>
                  <div class="col-6">
                    <div class="form-group">
                      <input
                        type="text"
                        class="form-control"
                        name="cpf"
                        :placeholder="translate.yourName"
                        v-model="nomeComment"
                      />
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="form-group">
                      <input
                        type="text"
                        class="form-control"
                        name="cpf"
                        :placeholder="translate.yourEmail"
                        v-model="emailComment"
                      />
                    </div>
                  </div>
                  <div class="col-lg-6 col-12 accept mt-3">
                    <input
                      type="checkbox"
                      name="acept"
                      id="acept"
                      v-model="newsletterComment"
                    />
                    <label for="checkbox">{{ translate.formNewsCheck }}</label>
                  </div>
                </div>
                <div class="mb-3">
                  <textarea
                    class="form-control"
                    id="text-comment-bella"
                    :placeholder="translate.inputComment"
                    rows="5"
                    v-model="comentario"
                  ></textarea>
                </div>
                <vue-recaptcha
                  ref="red"
                  v-if="showRecaptcha"
                  :sitekey="recaptchaSiteKey"
                  v-on:verify="recaptchaUpdated"
                  hl="pt-br"
                ></vue-recaptcha>
                <a
                  class="button mt-2"
                  href="#"
                  @click.prevent="saveComment(showModalMessages)"
                >
                  {{ translate.button }}
                  <i class="icon-arrow-right"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container">
        <div
          class="row pb-5 pt-5 d-flex align-items-center justify-content-center"
        >
          <BannerAssineHorizontal :path="bannerAssineHorizontal" />
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>
<style lang="css">
.meuBotao {
  background-color: black !important; /* Cor de fundo preta */
  color: white; /* Cor da fonte branca */
  font-size: 22px;
  font-weight: bold;
  border-radius: 22px; /* Tamanho da fonte */
  border: none; /* Remove a borda padrão */
  padding: 10px 20px; /* Espaçamento interno */
  cursor: pointer; /* Muda o cursor para indicar que é clicável */
  transition: background-color 0.3s; /* Transição suave para a cor de fundo */
}
.meuBotao:hover {
  background-color: #b20000 !important;
  opacity: 0.8; /* Efeito de clareamento */
}
/* Botão da live no topo do post: um link com o visual do .meuBotao dos posts.
   O texto em PT quebra em duas linhas no celular; as duas ficam centralizadas. */
a.meuBotao.botao-live,
a.meuBotao.botao-live:hover {
  display: inline-block;
  color: #ffffff;
  text-align: center;
  text-decoration: none;
}
</style>
<style scoped lang="css">
label {
  height: 23px;
  color: #5b5858;
  font-family: "Roboto-Regular";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 23px;
}

input[type="checkbox"] {
  position: relative;
  cursor: pointer;
  margin-inline: 15px;
  width: 20px;
  height: 20px;
}

input[type="checkbox"]:before {
  content: "";
  display: block;
  position: absolute;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  top: -3px;
  left: -3px;
  background-color: #000;
}

input[type="checkbox"]:checked:before {
  content: "";
  display: block;
  position: absolute;
  width: 30px;
  height: 30px;
  top: -3px;
  left: -3px;
  background-color: #000;
}

input[type="checkbox"]:checked:after {
  content: "";
  display: block;
  width: 9px;
  height: 15px;
  border: solid red;
  border-width: 0 2px 2px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
  position: absolute;
  top: 2px;
  left: 6px;
}

.form-group input {
  padding: 15px 20px;
  border-radius: 30px;
  background-color: #fff;
  font-size: small;
}

.form-control:focus {
  color: #000;
  background-color: #f8f8f8;
}

.img-banner-rodape {
  height: 124px;
  width: 1012px;
}

.button {
  height: 50px;
  width: 142px;
  margin-bottom: 56px;
  cursor: pointer;
  border-radius: 30px;
  margin-left: 10px;
}

.quest-title {
  color: #b20000;
  font-family: "Roboto-Medium";
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 28px;
  margin-bottom: 5px;
}

.quest-nickname {
  color: #5b5858;
  font-family: "Roboto-Regular";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 16px;
}

.form-control {
  margin-top: 10px;
  background-color: #fff;
  color: #868686;
  font-size: 14px;
  font-weight: 600;
  font-family: "Exo-2";
}

.card-comment > div {
  font-family: "Roboto-Regular";
  font-size: 13px;
}

.card-comment > legend {
  font-family: "Roboto-Regular";
  font-size: 16px;
  line-height: 19px;
}

/* Assinante Gold: texto do comentário em negrito. */
.card-comment > legend.texto-gold {
  font-family: "Roboto-Bold";
}

/* Links dentro do comentário (gerados por renderCommentHtml via v-html, por
   isso o :deep). URLs longas quebram em vez de estourar a caixa no mobile. */
.card-comment > legend :deep(a) {
  color: #b20000;
  text-decoration: underline;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.card-comment > div > span {
  font-family: "Roboto-Bold";
  font-size: 13px;
  color: #000;
}

.card-comment > hr {
  margin-bottom: 10px !important;
}

.answer {
  padding-left: 50px !important;
}

.text-answer {
  color: #b20000 !important;
  font-size: 14px;
  cursor: pointer;
  padding-left: 10px;
}

.slot-body {
  margin-top: 65px !important;
  background-color: #fff !important;
  text-align: justify;
}

#slot-post-api {
  max-width: 808px;
}

.list-comentarios {
  margin-top: 5px;
}

.box-title {
  max-width: 808px;
  padding-inline: 0;
}

.box-details {
  width: 808px;
  padding-inline: 0;
  margin-top: 20px;
}

.box-gray {
  background-color: #f8f8f8;
  width: 100%;
}

.box-details span {
  font-size: 16px;
  line-height: 19px;
  color: #666;
}

.box-w {
  margin-top: 50px;
  width: 808px;
  padding-inline: 0;
}

.box-w-2 {
  margin-top: 50px;
  width: 808px;
  padding-inline: 0;
}

.title-comments {
  color: #b20000;
  font-family: "Roboto-Medium";
  font-size: 24px;
  line-height: 28px;
  padding-inline: 0;
}

.card-text {
  font-family: "Roboto-Regular";
  font-size: 13px;
  line-height: 15px;
  color: #999;
}

.title {
  font-family: "Roboto-Medium";
  text-align: start;
  font-size: 36px;
  line-height: 66px;
  color: #b20000;
}

.card-comment img {
  width: 12px;
  margin-right: 5px;
  object-fit: cover;
}

/* Assinante Gold: ícone maior para dar ênfase ao comentário. */
.card-comment img.icone-gold {
  width: 12px;
  height: auto;
  vertical-align: -3px;
}

.button {
  height: 50px;
  width: 170px;
  margin-bottom: 56px;
  cursor: pointer;
  border-radius: 30px;
}

:deep(#slot-post-api > h2 > span) {
  font-size: 24px !important;
  color: #000;
  font-weight: 700;
}

:deep(span) {
  font-family: "Roboto-Regular", sans-serif !important;
}

:deep(strong) {
  font-family: "Roboto-Regular", sans-serif !important;
}

:deep(b) {
  font-family: "Roboto-Regular", sans-serif !important;
}

.alter-nickname {
  color: #b20000;
  cursor: pointer;
  font-size: 13px;
}

.share-align {
  display: flex;
  justify-content: flex-end !important;
}

.share-trigger {
  cursor: pointer;
}

/* Ancorado à direita (e à esquerda no mobile, onde o bloco alinha ao início)
   para o popup não estourar a largura da página. */
.share-popup {
  position: absolute;
  top: 30px;
  right: 0;
  background-color: white;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  z-index: 10;
  width: 350px;
  max-width: calc(100vw - 40px);
  padding: 20px;
  text-align: center;
}

.share-input {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  margin-right: 10px;
  width: 220px;
  max-width: 55%;
}

.share-button {
  font-weight: 500;
  background-color: black !important;
  border: 1px solid black !important;
  border-radius: 30px !important;
  color: white;
  font-family: "Exo-2";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
  height: 43px;
  padding: 0px 10px;
}

.share-close {
  position: absolute;
  top: 3px;
  right: 10px;
  font-size: 20px;
  font-weight: bold;
  color: #888;
  z-index: 11;
  cursor: pointer;
}

:deep(.iframe-video-blog) {
  max-width: 808px !important;
}

/* Vídeo do post, no desktop e no celular: nunca mais alto que a tela, descontados o
   header fixo (65px) e 16px acima e abaixo. O !important vence o width/height 100%
   que o editor grava inline. O vídeo em pé fica mais estreito, pela proporção que
   marcaProporcaoDosVideos() (utils/videoDoPost.ts) grava; o horizontal segue na
   largura do texto. Antes dos metadados, reserva o espaço de um vídeo 16:9, o
   formato da maioria, para o texto não pular. Mesma regra da coluna. */
:deep(#slot-post-api video) {
  display: block;
  width: 100% !important;
  height: auto !important;
  aspect-ratio: auto 16 / 9;
  max-height: calc(100vh - 97px);
  max-height: calc(100svh - 97px);
  max-width: calc((100svh - 97px) * var(--proporcao-video));
  margin: 0 auto;
}

:deep(#slot-post-api > div > img) {
  min-height: 500px;
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
  .title {
    font-family: "Roboto-Medium";
    text-align: start;
    font-size: 36px;
    line-height: 40px;
    color: #b20000;
  }

  #slot-post-api {
    max-width: 100%;
  }

  :deep(#slot-post-api > div > img) {
    min-height: 200px;
  }

  :deep(#slot-post-api img) {
    width: 100%;
  }

  .img-banner-rodape {
    height: unset;
    width: 90%;
  }

  .share-align {
    display: flex;
    justify-content: flex-start !important;
  }

  .share-popup {
    right: auto;
    left: 0;
  }

  :deep(.iframe-video-blog) {
    width: 100% !important;
    height: 300px !important;
  }
}
</style>
